import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { JsonViewer } from '@dataxpdtn/mui-json-viewer';
import config from '@iotv/config';
import { existsFilter, getRejectionBucketingFilter } from '@iotv/core-services';
import { classDerivedDeviceEnabledTypes, isClassDerivedDeviceEnabledType } from '@iotv/datamodel';
import { AutoBucketHistory, GroupClassName, } from '@iotv/datamodel-core';
import { AdjacentType, getZombieInstanceFromPrimaryKey, isValidBusinessObject, MetaStatus, Milliseconds, } from '@iotv/iotv-v3-types';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useEffect, useReducer, useState } from 'react';
import { Link } from 'react-router-dom';
import { ContainerArbitrator } from '../../../../../ArbitrationObjects/ContainerArbitrator/containerArbitrator';
import { ConsumerDao } from '../../../../../data/ConsumerDAO';
import { SectionControlIcons } from '../../../../../Iconography';
import { ObjectViewType } from '../../../../../types';
import { useColumnVisibilityForDataGrid } from '../../ObjectsDataTable/hooks/useColumnVisibility';
import { getAttributeSparkLineRenderer } from './cellRenderers';
import { incidentExtractor, notificationExtractor, nullExtractor, rulesetsExtractor } from './contextExtractors';
import { defaultState, GROUPING_PSEUDO_ROW_TYPE_ID, LazyQueryTableContext, LazyQueryTableReducer, ROOT_SEQUENCE_ID, ROOT_SEQUENCE_NAME, } from './QueryTableContext';
import { Row } from './Row';
dayjs.extend(utc);
const { REACT_APP_DEBUG } = process.env;
const debug = REACT_APP_DEBUG && false;
/** exists as a temporary switchover, to be removed when new style is confirmed */
const tableView = false;
/**
 * Inclusive list of types which will yield a queryable row
 *
 * The inverse (every other type), will be considered data which may or may not be used in subsequent data population, e.g. count of number of incidents
 *
 * This could be user-overrideable
 */
const lazyObjectTypes = new Set([
    GroupClassName,
    ...classDerivedDeviceEnabledTypes,
    ...Object.values(config.apps?.webUI?.domainMap ?? {})
        .map((d) => d.objectTypeIds)
        .flat(),
]);
export const lazyObjectQueryFilter = (query) => {
    const pass = query.path.some((step) => (step.adjacencyType === AdjacentType.CHILD && step.objectTypeId ? lazyObjectTypes.has(step.objectTypeId) : false));
    if (!pass)
        debug && console.info(`Filtering out query ${query.id} as it does not contain any steps with lazy loadable object types`);
    return pass;
};
export const LazyQueryTable = ({ queries, columnRenderers, datagridProps: datagridPropsIn, applicationData, userFunctions, isSmallView, }) => {
    const [state, dispatch] = useReducer(LazyQueryTableReducer, defaultState);
    useEffect(() => {
        dispatch({ type: 'SET_IS_SMALL_VIEW', payload: isSmallView });
    }, [isSmallView]);
    const apiRef = useGridApiRef();
    const hasGroupingFunction = queries.some((q) => typeof q.groupingFunction === 'function');
    /** the use of groupingFunction here is use case specific. It is optional */
    const queryAndAddRows = async ({ previousQueryLevel, previousSequenceId, previousSequenceName, query, contextObjects, groupingFunction, bucketStrategyMap, readingsAttributeKey, }) => {
        const { id: queryId, path, subQueries } = query;
        const rejectionsBucket = [];
        const immediateQueryFilter = getRejectionBucketingFilter({ filter: lazyObjectQueryFilter, rejectionsBucket });
        const nextQueries = subQueries.filter(immediateQueryFilter);
        const { err, data } = await ConsumerDao.getGraphQueryResponse({ contextObjects, path });
        if (err) {
            console.error(`Error executing query: ${err.message}`);
        }
        else if (data) {
            const items = [...data.lastNodesAndEdges.nodes, ...data.lastNodesAndEdges.edges];
            userFunctions.addObjectsToState(items);
            const newRows = data.lastNodesAndEdges.nodes
                .filter(isValidBusinessObject)
                .filter((vob) => (vob.type === GroupClassName ? lazyObjectTypes.has(vob.supportedType) : true))
                .map((item) => {
                let groupingPseudoRow = null;
                if (groupingFunction) {
                    /** use case-specific handling for grouping pseudo rows */
                    const res = groupingFunction(previousQueryLevel, previousSequenceId, item);
                    groupingPseudoRow = res.groupingPseudoRow;
                }
                const nextSequenceId = groupingPseudoRow ? `${groupingPseudoRow.sequenceId}\u001e${item.sk}` : `${previousSequenceId}\u001e${item.sk}`;
                const nextSquenceName = groupingPseudoRow ? `${groupingPseudoRow.sequenceName}\u001e${item.name}` : `${previousSequenceName}\u001e${item.name}`;
                const rowObject = {
                    ...item,
                    sequenceId: nextSequenceId,
                    sequenceName: nextSquenceName,
                    queryLevel: previousQueryLevel + 1,
                    contextObject: item,
                    visible: true,
                    expanded: false,
                    queries: nextQueries,
                    querying: false,
                    queryExecuted: false,
                    dailyReadings: null,
                    hourlyReadings: null,
                    bucketStrategyMap,
                    readingsAttributeKey,
                    isSmallView,
                };
                return [rowObject, groupingPseudoRow].filter(existsFilter);
            })
                .flat();
            /** get readings if `classDerivedDeviceEnabledType` */
            for (const row of newRows) {
                if (isClassDerivedDeviceEnabledType(row.contextObject))
                    handleGetBucketedReadings(row, bucketStrategyMap ?? {});
            }
            dispatch({ type: 'SET_EXECUTED', payload: { sequenceId: previousSequenceId } });
            dispatch({ type: 'ADD_ROWS', payload: newRows });
            const lastNodes = data.lastNodesAndEdges.nodes.filter(isValidBusinessObject);
            if (rejectionsBucket.length > 0 && lastNodes.length > 0) {
                for (const query of rejectionsBucket) {
                    debug && console.log(`executing rejection bucket query ${query.id} with context objects: `, lastNodes);
                    const { err, data } = await ConsumerDao.getGraphQueryRequestV2({ contextObjects: lastNodes, query: query });
                    if (err) {
                        console.error(`Error executing rejection bucket query ${query.id}: ${err.message}`);
                    }
                    else if (data) {
                        const items = data.getAllContents();
                        if (items.length > 0) {
                            debug && console.log(`Adding items from rejection bucket query ${query.id} to application data: `, data.getSummary());
                            userFunctions.addObjectsToState(items);
                        }
                    }
                }
            }
        }
    };
    const handleGetBucketedReadings = async (row, bucketStrategyMap) => {
        debug && console.log(`Temp getting bucketed readings for row ${row.sequenceId} with context object: `, row.contextObject);
        const { contextObject, dailyReadings, hourlyReadingsBucketed } = row;
        if (contextObject) {
            try {
                const nowjs = dayjs.utc();
                const fromTimestampMs = nowjs.subtract(1, 'month').valueOf();
                const toTimestampMs = nowjs.valueOf();
                const { data, err } = await ConsumerDao.getBucketedReadings({
                    entity: contextObject,
                    bucketStrategyMap,
                    fromTimestampMs,
                    toTimestampMs,
                    readingsRecentPeriodMs: Milliseconds.DAY,
                });
                if (err) {
                    console.error(`Error fetching bucketed readings for ${contextObject.id}: ${err.message}`);
                }
                else {
                    debug && console.log(`Bucketed readings for ${contextObject.id}: `, data);
                    if (data) {
                        const action = {
                            type: 'UPDATE_ROW',
                            payload: {
                                ...row,
                                dailyReadings: new AutoBucketHistory(data.daily),
                                hourlyReadings: new AutoBucketHistory(data.hourly),
                            },
                        };
                        dispatch(action);
                    }
                }
            }
            catch (error) {
                console.error(`Unexpected error fetching bucketed readings for ${contextObject.id}: `, error);
            }
        }
    };
    /**
     * If a clicked row is not flagged as having been queried, do query
     *
     * If not toggle row visibility
     */
    const handleRowClick = async (row) => {
        const { visible, queryExecuted, queries, contextObject, sequenceId, sequenceName, queryLevel, bucketStrategyMap, readingsAttributeKey, expanded } = row;
        if (visible && !queryExecuted && 0 < queries.length && contextObject) {
            dispatch({ type: 'UPDATE_ROW', payload: { ...row, querying: true } });
            for (const query of queries) {
                await queryAndAddRows({
                    previousQueryLevel: queryLevel,
                    previousSequenceId: sequenceId,
                    previousSequenceName: sequenceName,
                    query,
                    contextObjects: [contextObject],
                    bucketStrategyMap: bucketStrategyMap,
                    readingsAttributeKey,
                });
            }
        }
        else if (queryExecuted && expanded) {
            dispatch({ type: 'HIDE_SUB_ROW', payload: row });
        }
        else if (!expanded) {
            dispatch({ type: 'SHOW_SUB_ROW', payload: row });
        }
        else {
            debug && console.info(`Row clicked with no action: `, row);
        }
    };
    /**
     * On first render, use the root objects as input to the first query of the input branching query definition
     *
     * If the first step is a `SUBJECT` step, (as would be the case for some auto generated queries) use the next sub queries, (as would be passed down in queryAndAddRows)
     *
     * This avoids adding the second step which is the self step and would just create a duplicate row for each root object.
     */
    useEffect(() => {
        for (const { query, rootObjects, groupingFunction, bucketStrategyMap, readingsAttributeKey } of queries) {
            const { path, subQueries } = query;
            if (path.at(0)?.adjacencyType === AdjacentType.SUBJECT) {
                // auto generated queries have a self step which we want to skip as context is represented by the page
                for (const query of subQueries) {
                    queryAndAddRows({
                        previousQueryLevel: 0,
                        previousSequenceId: ROOT_SEQUENCE_ID,
                        previousSequenceName: ROOT_SEQUENCE_NAME,
                        query,
                        contextObjects: rootObjects,
                        groupingFunction,
                        bucketStrategyMap,
                        readingsAttributeKey,
                    });
                }
            }
            else {
                queryAndAddRows({
                    previousQueryLevel: 0,
                    previousSequenceId: ROOT_SEQUENCE_ID,
                    previousSequenceName: ROOT_SEQUENCE_NAME,
                    query,
                    contextObjects: rootObjects,
                    groupingFunction,
                    bucketStrategyMap,
                    readingsAttributeKey,
                });
            }
        }
    }, []);
    /** render the +/- control, and inject click handler, used on first column */
    const lazyQueryRenderer = ({ row }) => {
        return _jsx(ExpandCollapse, { row, onClick: () => handleRowClick(row) });
    };
    /** curry function that produces a renderCell. It injects nodeEdgeMapperState and contextObject into the ContextValueExtractor which are not available to query definers */
    const getXRenderCell = (extractor) => (params) => {
        const render = extractor?.(params.row, params.row.contextObject, applicationData);
        return render ? render(params) : null;
    };
    const getInternalRenderCell = (columnKey) => (params) => {
        const objectTypeId = params.row.contextObject.type;
        const extractor = queries.find((q) => q.dashboardObjectTypeId === objectTypeId)?.cellRenderers?.[columnKey]?.contextValueExtractor ?? nullExtractor;
        const render = extractor(params.row, params.row.contextObject, applicationData);
        return render ? render(params) : null;
    };
    const maxQueryLevel = Math.max(0, ...state.rows.filter((row) => row.visible && row.queries.filter(lazyObjectQueryFilter).length).map((row) => row.queryLevel)) + 1;
    /** DataGrid input */
    const definedFields = [
        'query',
        GROUPING_PSEUDO_ROW_TYPE_ID,
        'name',
        '_rulesets',
        '_incidents',
        'value',
        'score',
        '_lastNotification',
        'dailyReadings',
        'hourlyReadings',
    ];
    const allFields = [...definedFields, ...Object.keys(columnRenderers)];
    /** if no queries define a groupingFn don't display the grouping column */
    const columnVisibilityModelBase = {
        ...allFields.reduce((acc, field) => ({ ...acc, [field]: false }), {}),
        query: true,
        [GROUPING_PSEUDO_ROW_TYPE_ID]: hasGroupingFunction,
        name: !hasGroupingFunction,
    };
    const [columnVisibilityModel, setColumnVisibilityModel, onColumnVisibilityModelChange] = useColumnVisibilityForDataGrid({
        dataTableReference: 'LazyQueryTable',
        externalColumnVisibility: columnVisibilityModelBase,
        objectTypeId: undefined,
        viewType: ObjectViewType.List,
    });
    const grouping = ['Context', 'Readings'];
    const groupCollapseMaps = {
        Context: ['_rulesets'],
        Readings: ['_incidents', '_lastNotification', 'value', 'score', ...Object.keys(columnRenderers), 'dailyReadings', 'hourlyReadings'],
    };
    const [columnCollapseState, setColumnCollapseState] = useState({
        groups: {
            Context: 'Collapsed',
            Readings: 'Collapsed',
        },
    });
    const columnVisiblitySetter = (columnGroup) => {
        const toggleFields = groupCollapseMaps[columnGroup];
        const newCollapseState = columnCollapseState.groups[columnGroup] === 'Collapsed' ? 'Expanded' : 'Collapsed';
        const show = newCollapseState === 'Expanded';
        setColumnCollapseState((prev) => ({ groups: { ...prev.groups, [columnGroup]: newCollapseState } }));
        const explicitSets = Object.keys(columnVisibilityModel).reduce((acc, key) => {
            if (toggleFields.includes(key)) {
                acc[key] = show;
            }
            return acc;
        }, {});
        const newModel = { ...columnVisibilityModel, ...explicitSets };
        setColumnVisibilityModel(newModel);
    };
    const getColumnGroupToggler = ({ title, columnGroup }) => {
        const onClick = () => columnVisiblitySetter(columnGroup);
        return (_jsxs(Stack, { flexGrow: 1, direction: "row", justifyContent: "center", alignItems: "center", gap: 1, children: [title ? _jsx(Typography, { children: title }) : null, columnCollapseState.groups[columnGroup] === 'Collapsed' ? (_jsx(SectionControlIcons.Expand, { onClick })) : (_jsx(SectionControlIcons.Collapse, { onClick }))] }));
    };
    const { rows } = state;
    const visibleRows = rows.filter((row) => row.visible);
    const common = {
        sortable: false,
        resizable: false,
        disableColumnMenu: true,
    };
    const developmentColumns = debug
        ? [
            { ...common, field: 'sequenceId', headerName: 'Sequence ID', width: 450 },
            { ...common, field: 'type', headerName: 'type', width: 160 },
            { ...common, field: 'id', headerName: 'ID', width: 280 },
            { ...common, field: 'queryExecuted', headerName: 'Exec', width: 50, align: 'center', renderCell: ({ value }) => (value ? '✅' : '⏳') },
        ]
        : [];
    const columns = [
        { ...common, field: 'query', type: 'actions', headerName: '', width: maxQueryLevel * (!isSmallView ? 24 : 36), renderCell: lazyQueryRenderer },
        { ...common, field: GROUPING_PSEUDO_ROW_TYPE_ID, headerName: 'Name', minWidth: 200, flex: !isSmallView ? undefined : 1, resizable: true, renderCell: Main },
        { ...common, field: 'name', headerName: 'Name', minWidth: 200, flex: !isSmallView ? undefined : 1, resizable: true, renderCell: Main },
        { ...common, field: '_rulesets', headerName: 'Rules', minWidth: 150, flex: 1, renderCell: getXRenderCell(rulesetsExtractor) },
        { ...common, field: '_incidents', headerName: 'Incidents', width: 80, renderCell: getXRenderCell(incidentExtractor) },
        { ...common, field: '_lastNotification', headerName: 'Notification', width: 150, renderCell: getXRenderCell(notificationExtractor) },
        // the external column renderers
        { ...common, field: 'value', headerName: 'Value', width: 65, renderCell: getInternalRenderCell('value') },
        { ...common, field: 'score', headerName: 'Score', width: 65, renderCell: getInternalRenderCell('score') },
        ...Object.entries(columnRenderers).map(([field, { width, relativeFlex, contextValueExtractor }]) => ({
            ...common,
            field,
            headerName: field,
            width,
            flex: relativeFlex,
            renderCell: getXRenderCell(contextValueExtractor) ?? undefined,
        })),
        { ...common, field: 'dailyReadings', headerName: 'Daily Readings', width: 250, renderCell: getAttributeSparkLineRenderer('dailyReadings') },
        { ...common, field: 'hourlyReadings', headerName: 'Hourly Readings', width: 250, renderCell: getAttributeSparkLineRenderer('hourlyReadings') },
        ...developmentColumns,
    ];
    const datagridProps = {
        ...datagridPropsIn,
        apiRef,
        columns,
        columnHeaderHeight: !isSmallView ? 36 : 54,
        columnVisibilityModel,
        rows: visibleRows,
        rowHeight: !isSmallView ? 36 : 54,
        getRowId: (row) => row.sequenceId,
        hideFooter: true,
        density: 'compact',
        disableRowSelectionOnClick: true,
        sx: { [`& .MuiDataGrid-cell`]: { [`&[data-field^="query"], &[data-field^="${GROUPING_PSEUDO_ROW_TYPE_ID}"], &[data-field^="name"]`]: { padding: 0 } } },
    };
    return (_jsx(LazyQueryTableContext.Provider, { value: { state, dispatch }, children: _jsxs(Stack, { gap: 1, children: [_jsx(Stack, { alignItems: "stretch", gap: 1, children: visibleRows.map((r) => (_jsx(Row, { row: r, handleRowClick: handleRowClick, queries: queries, columnRenderers: columnRenderers, applicationData: applicationData }, r.sequenceId))) }), tableView && (_jsxs(_Fragment, { children: [_jsxs(Stack, { direction: "row", children: [getColumnGroupToggler({ title: 'Context', columnGroup: 'Context' }), getColumnGroupToggler({ title: 'Readings', columnGroup: 'Readings' })] }), _jsx(DataGrid, { ...datagridProps })] })), debug && _jsx(JsonViewer, { name: 'Application Data', rootName: 'Application Data', value: applicationData.getSummary() })] }) }));
};
const ExpandCollapse = ({ row, onClick }) => {
    const { queries, type, expanded, querying, isSmallView } = row;
    const hasSubQueries = 0 < queries.filter(lazyObjectQueryFilter).length;
    if (hasSubQueries || type === GROUPING_PSEUDO_ROW_TYPE_ID) {
        return (_jsxs(Stack, { direction: "row", width: "100%", alignItems: "center", onClick: !querying ? onClick : undefined, sx: { paddingInlineStart: row.queryLevel ? (!querying ? row.queryLevel : row.queryLevel - 1) * (!isSmallView ? 3 : 4.5) : 0 }, children: [querying && _jsx(CircularProgress, { size: !isSmallView ? 18 : 24, sx: { marginInline: !isSmallView ? '3px' : '6px' } }), expanded ? (_jsx(SectionControlIcons.Collapse, { opacity: querying ? 0.5 : undefined, fontSize: !isSmallView ? 'medium' : 'large' })) : (_jsx(SectionControlIcons.Expand, { opacity: querying ? 0.5 : undefined, fontSize: !isSmallView ? 'medium' : 'large' }))] }));
    }
    else {
        return null;
    }
};
const Main = (props) => {
    const { row, formattedValue, value } = props;
    const text = formattedValue ?? value;
    const isPseudoRow = row.type === GROUPING_PSEUDO_ROW_TYPE_ID;
    const state = row.metadata?.status;
    const sk = props.row.sk;
    const vob = sk ? getZombieInstanceFromPrimaryKey(sk, { supportedType: props?.row?.supportedType }) : undefined;
    const to = vob ? ContainerArbitrator.getCanonicalContainerLinkForObject({ vob }) : undefined;
    return (_jsx(Stack, { width: "100%", height: "100%", sx: {
            fontSize: !row.isSmallView ? 16 : 24,
            marginInlineStart: row.queryLevel * (!row.isSmallView ? 1.5 : 2.25),
            paddingInline: 1.5,
            color: (t) => state !== undefined
                ? state === MetaStatus.Normal
                    ? t.palette.info.contrastText
                    : t.palette.error.contrastText
                : t.palette.getContrastText(t.palette.grey[300]),
            backgroundColor: (t) => (state !== undefined ? (state === MetaStatus.Normal ? t.palette.info.dark : t.palette.error.dark) : t.palette.grey[300]),
        }, children: !isPseudoRow && to ? (_jsx(Link, { to: to, style: { color: 'inherit' }, children: _jsx(Typography, { style: { fontSize: 'inherit', color: 'inherit', fontWeight: 'bolder' }, children: text }) })) : (_jsx(Typography, { style: { fontSize: 'inherit', color: 'inherit', fontWeight: 'bolder' }, children: text })) }));
};
